import React from 'react';
import { X, SlidersHorizontal, ChevronDown, ChevronUp, Check, RotateCcw, Building2, User, Phone, Mail, Cpu, MapPin } from 'lucide-react';
import { useThemeStore } from '@/stores';
import { useLocations } from '@/hooks/useData';

export interface SubscriberFilterModel {
  customerId: string;
  name: string;
  phone: string;
  email: string;
  ontSerial: string;
  zoneId: string;
  zone: string;
  includeSubPartners: boolean;
}

export const initialSubscriberFilter: SubscriberFilterModel = {
  customerId: '',
  name: '',
  phone: '',
  email: '',
  ontSerial: '',
  zoneId: '',
  zone: '',
  includeSubPartners: false,
};

export function countActiveFilters(filters: SubscriberFilterModel): number {
  let count = 0;
  if (filters.customerId?.trim()) count++;
  if (filters.name?.trim()) count++;
  if (filters.phone?.trim()) count++;
  if (filters.email?.trim()) count++;
  if (filters.ontSerial?.trim()) count++;
  if (filters.zoneId?.trim() || filters.zone?.trim()) count++;
  if (filters.includeSubPartners) count++;
  return count;
}

interface SubscriberFilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  filters: SubscriberFilterModel;
  onApply: (filters: SubscriberFilterModel) => void;
  onReset: () => void;
}

export interface ZoneOption {
  id: string;
  name: string;
  code: string;
}

export const DEFAULT_PORTAL_ZONES: ZoneOption[] = [
  { id: 'FNS0026', name: 'FNS0026', code: 'FNS0026' },
  { id: 'FNS0028', name: 'FNS0028', code: 'FNS0028' },
  { id: 'FNS0029', name: 'FNS0029', code: 'FNS0029' },
  { id: 'FNS0030', name: 'FNS0030', code: 'FNS0030' },
  { id: 'FNS0036', name: 'FNS0036', code: 'FNS0036' },
  { id: 'FNS0037', name: 'FNS0037', code: 'FNS0037' },
  { id: 'FNS0038', name: 'FNS0038', code: 'FNS0038' },
  { id: 'FNS0039', name: 'FNS0039', code: 'FNS0039' },
  { id: 'FNS0040', name: 'FNS0040', code: 'FNS0040' },
  { id: 'FNS0041', name: 'FNS0041', code: 'FNS0041' },
  { id: 'FNS0046', name: 'FNS0046', code: 'FNS0046' },
  { id: 'FNS0047', name: 'FNS0047', code: 'FNS0047' },
  { id: 'FNS0060', name: 'FNS0060', code: 'FNS0060' },
  { id: 'FNS0061', name: 'FNS0061', code: 'FNS0061' },
  { id: 'FNS0073', name: 'FNS0073', code: 'FNS0073' },
  { id: 'FNS0083', name: 'FNS0083', code: 'FNS0083' },
  { id: 'FNS0085', name: 'FNS0085', code: 'FNS0085' },
  { id: 'FNS0087', name: 'FNS0087', code: 'FNS0087' },
  { id: 'FNS0089', name: 'FNS0089', code: 'FNS0089' },
  { id: 'FNS0092', name: 'FNS0092', code: 'FNS0092' },
  { id: 'FNS0096', name: 'FNS0096', code: 'FNS0096' },
  { id: 'FNS0098', name: 'FNS0098', code: 'FNS0098' },
  { id: 'FNS0099', name: 'FNS0099', code: 'FNS0099' },
  { id: 'FNS0100', name: 'FNS0100', code: 'FNS0100' },
  { id: 'FNS0102', name: 'FNS0102', code: 'FNS0102' },
  { id: 'FNS0103', name: 'FNS0103', code: 'FNS0103' },
  { id: 'FNS0110', name: 'FNS0110', code: 'FNS0110' },
  { id: 'FNS0111', name: 'FNS0111', code: 'FNS0111' },
  { id: 'FNS0113', name: 'FNS0113', code: 'FNS0113' },
  { id: 'FNS0115', name: 'FNS0115', code: 'FNS0115' },
  { id: 'FNS0117', name: 'FNS0117', code: 'FNS0117' },
  { id: 'FNS0142', name: 'FNS0142', code: 'FNS0142' },
  { id: 'FNS0169', name: 'FNS0169', code: 'FNS0169' },
  { id: 'FNS0173', name: 'FNS0173', code: 'FNS0173' },
  { id: 'FNS0174', name: 'FNS0174', code: 'FNS0174' },
  { id: 'FNS0387', name: 'FNS0387', code: 'FNS0387' },
  { id: 'FNS0388', name: 'FNS0388', code: 'FNS0388' },
  { id: 'FNS0389', name: 'FNS0389', code: 'FNS0389' },
  { id: 'FNS0393', name: 'FNS0393', code: 'FNS0393' },
  { id: 'FNS0394', name: 'FNS0394', code: 'FNS0394' },
];

export function extractZoneOption(z: any): ZoneOption | null {
  if (!z) return null;
  if (typeof z === 'string') {
    const val = z.trim();
    if (!val || val === 'undefined' || val === 'null') return null;
    return { id: val, name: val, code: val };
  }

  // 1. Check self object (Portal standard)
  const selfDisplay = z.self?.displayValue;
  const selfId = z.self?.id;

  // 2. Check direct fields
  const directCode = z.code || z.zone_code || z.zoneCode;
  const directName = z.name || z.zoneName || z.displayValue;
  const directId = z.id || z.zoneId || z.zone_id;

  // Resolve best display name
  let candidateName = selfDisplay || directCode || directName || selfId || directId;
  if (!candidateName || typeof candidateName !== 'string' || candidateName.trim() === '' || candidateName === 'undefined' || candidateName === 'null') {
    if (typeof candidateName === 'number') {
      candidateName = `FNS${candidateName}`;
    } else {
      return null;
    }
  }

  const cleanName = String(candidateName).trim();
  const cleanId = String(selfDisplay || directCode || selfId || directId || cleanName).trim();

  return {
    id: cleanId,
    name: cleanName,
    code: String(directCode || cleanName).trim(),
  };
}

export const SubscriberFilterDrawer: React.FC<SubscriberFilterDrawerProps> = ({
  isOpen,
  onClose,
  filters,
  onApply,
  onReset,
}) => {
  const { theme } = useThemeStore();
  const isLight = theme === 'light';

  // Draft local state while drawer is open
  const [draftFilters, setDraftFilters] = React.useState<SubscriberFilterModel>(filters);

  // Collapsible sections
  const [subscriberInfoOpen, setSubscriberInfoOpen] = React.useState(true);
  const [subPartnersOpen, setSubPartnersOpen] = React.useState(true);

  // Locations / Zones
  const { data: zonesData, isLoading: isZonesLoading } = useLocations();

  const zonesList = React.useMemo<ZoneOption[]>(() => {
    const map = new Map<string, ZoneOption>();

    // 1. Process server zonesData
    if (zonesData) {
      let rawItems: any[] = [];
      if (Array.isArray(zonesData)) {
        rawItems = zonesData;
      } else if (typeof zonesData === 'object') {
        const d = zonesData as any;
        rawItems = d.items || d.data || d.model?.items || d.model || [];
      }

      if (Array.isArray(rawItems)) {
        rawItems.forEach((item) => {
          const parsed = extractZoneOption(item);
          if (parsed && parsed.name && parsed.name !== 'undefined' && parsed.name !== 'null') {
            map.set(parsed.name, parsed);
          }
        });
      }
    }

    // 2. If no server items or to ensure all contractor coverage zones are selectable
    if (map.size === 0) {
      DEFAULT_PORTAL_ZONES.forEach((z) => map.set(z.name, z));
    }

    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name, 'ar-IQ', { numeric: true })
    );
  }, [zonesData]);

  // Sync draft when opened or filters change
  React.useEffect(() => {
    if (isOpen) {
      setDraftFilters(filters);
    }
  }, [isOpen, filters]);

  // Lock body scroll when open
  React.useEffect(() => {
    if (isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isOpen]);

  // Escape key handler
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleFieldChange = (field: keyof SubscriberFilterModel, value: any) => {
    setDraftFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApply = () => {
    onApply(draftFilters);
    onClose();
  };

  const handleClearAll = () => {
    setDraftFilters(initialSubscriberFilter);
    onReset();
    onClose();
  };

  const activeCount = countActiveFilters(draftFilters);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: 'flex',
        justifyContent: 'flex-start',
        direction: 'rtl',
      }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.65)',
          backdropFilter: 'blur(3px)',
          animation: 'fadeIn 0.2s ease-out',
        }}
      />

      {/* Drawer Container */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '420px',
          height: '100%',
          background: isLight ? '#ffffff' : '#0b192e',
          borderLeft: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '-8px 0 28px rgba(0, 0, 0, 0.4)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 10000,
          animation: 'slideInRight 0.25s ease-out',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255, 255, 255, 0.08)',
            background: isLight ? '#f8fafc' : 'rgba(15, 23, 42, 0.6)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: 'rgba(239, 68, 68, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ef4444',
              }}
            >
              <SlidersHorizontal size={18} />
            </div>
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: '17px',
                  fontWeight: 700,
                  color: isLight ? '#0f172a' : '#f8fafc',
                }}
              >
                تصفية
              </h2>
              {activeCount > 0 && (
                <span
                  style={{
                    fontSize: '11px',
                    color: '#ef4444',
                    fontWeight: 600,
                  }}
                >
                  {activeCount} خيارات مفعلة
                </span>
              )}
            </div>
          </div>

          <button
            onClick={onClose}
            aria-label="إغلاق التصفية"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: isLight ? '#64748b' : '#94a3b8',
              padding: '6px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Form Body */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          {/* Section 1: معلومات المشترك */}
          <div
            style={{
              borderRadius: '10px',
              border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255, 255, 255, 0.08)',
              background: isLight ? '#ffffff' : 'rgba(30, 41, 59, 0.4)',
              overflow: 'hidden',
            }}
          >
            <button
              onClick={() => setSubscriberInfoOpen(!subscriberInfoOpen)}
              type="button"
              style={{
                width: '100%',
                padding: '12px 14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: isLight ? '#f1f5f9' : 'rgba(255, 255, 255, 0.03)',
                border: 'none',
                cursor: 'pointer',
                color: isLight ? '#1e293b' : '#e2e8f0',
                fontWeight: 600,
                fontSize: '14px',
                textAlign: 'right',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <User size={16} color="#3b82f6" />
                <span>معلومات المشترك</span>
              </div>
              {subscriberInfoOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>

            {subscriberInfoOpen && (
              <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* 1. معرف المشترك */}
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: 600,
                      marginBottom: '6px',
                      color: isLight ? '#475569' : '#94a3b8',
                    }}
                  >
                    معرف المشترك
                  </label>
                  <input
                    type="text"
                    value={draftFilters.customerId}
                    onChange={(e) => handleFieldChange('customerId', e.target.value)}
                    placeholder="معرف المشترك"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255, 255, 255, 0.12)',
                      background: isLight ? '#ffffff' : 'rgba(15, 23, 42, 0.6)',
                      color: isLight ? '#0f172a' : '#f8fafc',
                      fontSize: '13px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                {/* 2. اسم المشترك */}
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: 600,
                      marginBottom: '6px',
                      color: isLight ? '#475569' : '#94a3b8',
                    }}
                  >
                    اسم المشترك
                  </label>
                  <input
                    type="text"
                    value={draftFilters.name}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    placeholder="اسم المشترك"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255, 255, 255, 0.12)',
                      background: isLight ? '#ffffff' : 'rgba(15, 23, 42, 0.6)',
                      color: isLight ? '#0f172a' : '#f8fafc',
                      fontSize: '13px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                {/* 3. رقم هاتف المشترك */}
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: 600,
                      marginBottom: '6px',
                      color: isLight ? '#475569' : '#94a3b8',
                    }}
                  >
                    رقم هاتف المشترك
                  </label>
                  <input
                    type="text"
                    value={draftFilters.phone}
                    onChange={(e) => handleFieldChange('phone', e.target.value)}
                    placeholder="رقم هاتف المشترك"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255, 255, 255, 0.12)',
                      background: isLight ? '#ffffff' : 'rgba(15, 23, 42, 0.6)',
                      color: isLight ? '#0f172a' : '#f8fafc',
                      fontSize: '13px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                {/* 4. البريد الالكتروني للمشترك */}
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: 600,
                      marginBottom: '6px',
                      color: isLight ? '#475569' : '#94a3b8',
                    }}
                  >
                    البريد الالكتروني للمشترك
                  </label>
                  <input
                    type="text"
                    value={draftFilters.email}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    placeholder="البريد الالكتروني للمشترك"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255, 255, 255, 0.12)',
                      background: isLight ? '#ffffff' : 'rgba(15, 23, 42, 0.6)',
                      color: isLight ? '#0f172a' : '#f8fafc',
                      fontSize: '13px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                {/* 5. تسلسل ONT للمشترك */}
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: 600,
                      marginBottom: '6px',
                      color: isLight ? '#475569' : '#94a3b8',
                    }}
                  >
                    تسلسل ONT للمشترك
                  </label>
                  <input
                    type="text"
                    value={draftFilters.ontSerial}
                    onChange={(e) => handleFieldChange('ontSerial', e.target.value)}
                    placeholder="تسلسل ONT للمشترك"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255, 255, 255, 0.12)',
                      background: isLight ? '#ffffff' : 'rgba(15, 23, 42, 0.6)',
                      color: isLight ? '#0f172a' : '#f8fafc',
                      fontSize: '13px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Section 2: تضمين الشركاء الفرعيين & المنطقة */}
          <div
            style={{
              borderRadius: '10px',
              border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255, 255, 255, 0.08)',
              background: isLight ? '#ffffff' : 'rgba(30, 41, 59, 0.4)',
              overflow: 'hidden',
            }}
          >
            <button
              onClick={() => setSubPartnersOpen(!subPartnersOpen)}
              type="button"
              style={{
                width: '100%',
                padding: '12px 14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: isLight ? '#f1f5f9' : 'rgba(255, 255, 255, 0.03)',
                border: 'none',
                cursor: 'pointer',
                color: isLight ? '#1e293b' : '#e2e8f0',
                fontWeight: 600,
                fontSize: '14px',
                textAlign: 'right',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Building2 size={16} color="#10b981" />
                <span>تضمين الشركاء الفرعيين</span>
              </div>
              {subPartnersOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>

            {subPartnersOpen && (
              <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {/* Checkbox / Toggle for includeSubPartners */}
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={draftFilters.includeSubPartners}
                    onChange={(e) => handleFieldChange('includeSubPartners', e.target.checked)}
                    style={{
                      width: '18px',
                      height: '18px',
                      accentColor: '#ef4444',
                      cursor: 'pointer',
                    }}
                  />
                  <span
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: isLight ? '#1e293b' : '#e2e8f0',
                    }}
                  >
                    تضمين الشركاء الفرعيين (عرض مشتركي الشبكة الفرعية)
                  </span>
                </label>

                {/* Dropdown for المنطقة (Zone) */}
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: 600,
                      marginBottom: '6px',
                      color: isLight ? '#475569' : '#94a3b8',
                    }}
                  >
                    المنطقة
                  </label>
                  <select
                    value={draftFilters.zoneId || draftFilters.zone}
                    onChange={(e) => {
                      const val = e.target.value;
                      handleFieldChange('zoneId', val);
                      handleFieldChange('zone', val);
                    }}
                    style={{
                      width: '100%',
                      padding: '9px 12px',
                      borderRadius: '8px',
                      border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255, 255, 255, 0.12)',
                      background: isLight ? '#ffffff' : '#0f172a',
                      color: isLight ? '#0f172a' : '#f8fafc',
                      fontSize: '13px',
                      outline: 'none',
                      cursor: 'pointer',
                      boxSizing: 'border-box',
                    }}
                  >
                    <option value="">اختر المنطقة (الكل)</option>
                    {zonesList.map((z: any) => (
                      <option key={z.id || z.code} value={z.name || z.id}>
                        {z.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div
          style={{
            padding: '16px 20px',
            borderTop: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255, 255, 255, 0.08)',
            background: isLight ? '#f8fafc' : 'rgba(15, 23, 42, 0.6)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          {/* Apply Filter Button */}
          <button
            onClick={handleApply}
            type="button"
            style={{
              flex: 1,
              padding: '10px 16px',
              borderRadius: '8px',
              background: '#ef4444',
              color: '#ffffff',
              border: 'none',
              fontWeight: 700,
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              boxShadow: '0 2px 8px rgba(239, 68, 68, 0.35)',
              transition: 'background 0.15s ease',
            }}
          >
            <Check size={16} />
            <span>تطبيق التصفية</span>
          </button>

          {/* Clear All Button */}
          <button
            onClick={handleClearAll}
            type="button"
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              background: 'transparent',
              color: isLight ? '#64748b' : '#94a3b8',
              border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255, 255, 255, 0.15)',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.15s ease',
            }}
          >
            <RotateCcw size={14} />
            <span>مسح الكل</span>
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};
